import { FC, useState } from "react";
import {
  Button,
  Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import LOGIN_BG from "./../../../assets/images/login/login-bg.png";
import APP_LOGO from "./../../../assets/images/app-logo.png";
import { Lock, Person } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { pages } from "../../../utils/common";
import { getAlertBox, getMasterProgressBar } from "../../../utils/helpers_ui";
import { LoginViewProps } from "./login.interfaces";
import { CONSTANTS } from "../../../utils/CONSTS";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useAuthUserActions,
  useAuthUserState,
} from "../../../context/authUser.context";
import { IAuthParams } from "../../../apis/auth/auth.interfaces";
import { usePublicBrandingHook } from "../../../hooks/usePublicBrandingHook";
import { localStorageGet } from "../../../utils/localStorage";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(3, "Password must be at least 3 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginView: FC<LoginViewProps> = () => {
  const navigate = useNavigate();
  const { loginUserApiCall, logout } = useAuthUserActions();
  const { loggedUserAppState } = useAuthUserState();
  const publicBranding = usePublicBrandingHook();
  const [roleError, setRoleError] = useState<string>("");

  const logoSrc = publicBranding?.logoUrl ?? APP_LOGO;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = async (data: IAuthParams) => {
    setRoleError("");
    try {
      await loginUserApiCall(data);
      const userDetails = localStorageGet("userDetails") as any;
      const role = userDetails?.role;
      if (role !== "super_admin" && role !== "admin_role") {
        logout();
        setRoleError("Access denied. Only institute admins can log in here.");
        return;
      }
      navigate(pages.dashboard.path);
    } catch (error) {
      // Error is handled by context
      console.error(error);
    }
  };

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.login} />
      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "stretch",
          minHeight: "100%",
        }}
        data-testid="login-view"
      >
        {loggedUserAppState.isLoading && getMasterProgressBar()}
        <Grid
          item
          xs={6}
          sx={{
            backgroundColor: "white",
          }}
          data-testid="login-form-container"
        >
          <form
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: `100%`,
              flexDirection: "column",
            }}
            onSubmit={handleSubmit(onSubmit)}
            data-testid="login-form"
          >
            <Grid
              container
              spacing={2}
              sx={{
                px: 6,
                py: 4,
                width: `60% !important`,
                maxWidth: 400,
                alignItems: "center",
                justifyContent: "center",
                minWidth: 250,
                borderRadius: 2,
                boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
              }}
              data-testid="login-form-content"
            >
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <img src={logoSrc} alt={publicBranding?.instituteName ?? 'app-logo'} height={100} />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <Typography variant="h4" sx={{ textAlign: "center" }}>
                  Sign In
                </Typography>
              </Grid>
              {(loggedUserAppState.errorMessage || roleError) ? (
                <Grid item xs={12}>
                  {getAlertBox(
                    "Error",
                    roleError || loggedUserAppState.errorMessage || "Login failed!",
                    "error",
                  )}
                </Grid>
              ) : null}
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <ControlledTextField
                  control={control as any}
                  id="username"
                  name="username"
                  label="Username"
                  isGridItem={false}
                  errorMessage={errors.username?.message}
                  helperText={errors.username?.message || "Enter the User name"}
                  error={!!errors.username}
                  variant="standard"
                  size="medium"
                  sx={{ width: `100%` }}
                  data-testid="login-username-input"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <ControlledTextField
                  control={control as any}
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  isGridItem={false}
                  errorMessage={errors.password?.message}
                  helperText={
                    errors.password?.message ||
                    "Enter the Password. Minimum 3 characters."
                  }
                  error={!!errors.password}
                  variant="standard"
                  size="medium"
                  sx={{ width: `100%` }}
                  data-testid="login-password-input"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <Button
                  id="submitForm"
                  variant="contained"
                  sx={{ marginTop: 2, width: `100%` }}
                  type="submit"
                  disabled={isSubmitting || loggedUserAppState.isLoading}
                  data-testid="login-submit-button"
                >
                  {isSubmitting || loggedUserAppState.isLoading
                    ? "Logging in..."
                    : "Login"}
                </Button>
                <Link to={"/"} data-testid="forgot-password-link">
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      textDecoration: "underline",
                      textAlign: "center",
                      mt: 1,
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Link>
              </Grid>
            </Grid>
          </form>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            backgroundImage: `url(${LOGIN_BG})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `cover`,
            backgroundPosition: "center",
          }}
        />
      </Grid>
    </>
  );
};

export default LoginView;
